import { useState, useEffect, useMemo, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import type { Contact, Group } from '@/types';
import ContactModal from './components/ContactModal';
import DeleteModal from './components/DeleteModal';
import ImportModal from './components/ImportModal';
import GoogleContactsModal from './components/GoogleContactsModal';
import BulkEditModal from './components/BulkEditModal';
import ActionMenu from '@/components/base/ActionMenu';
import { normalizeBrazilianPhone, BRAZIL_DDDS, DEFAULT_DDD } from '@/lib/brazilDDDs';

const PAGE_SIZE = 50;

export default function ContactsPage() {
  const { profile } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const [contacts, setContacts] = useState<Contact[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [groups, setGroups] = useState<Group[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [search, setSearch] = useState('');
  const [groupFilter, setGroupFilter] = useState('');
  const [tagFilter, setTagFilter] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [page, setPage] = useState(1);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingContact, setEditingContact] = useState<Contact | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Contact | null>(null);
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [googleModalOpen, setGoogleModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [recentlyUpdated, setRecentlyUpdated] = useState<Set<string>>(new Set());
  const [selectAllMode, setSelectAllMode] = useState(false);
  const [deleteProgress, setDeleteProgress] = useState<{ current: number; total: number } | null>(null);
  const [deleteAllConfirmOpen, setDeleteAllConfirmOpen] = useState(false);
  const [deleteAllTarget, setDeleteAllTarget] = useState<'selected' | 'all' | null>(null);
  const [bulkEditOpen, setBulkEditOpen] = useState(false);
  const [bulkEditProgress, setBulkEditProgress] = useState<{ current: number; total: number } | null>(null);
  const [importProgress, setImportProgress] = useState<{ current: number; total: number } | null>(null);
  const [bulkCountryCodeOpen, setBulkCountryCodeOpen] = useState(false);
  const [fixAllCountryCodeOpen, setFixAllCountryCodeOpen] = useState(false);
  const [fixAllProgress, setFixAllProgress] = useState(false);
  const [fixAllResult, setFixAllResult] = useState<{ updated: number; skipped: number; errored: number; total: number } | null>(null);

  const companyId = profile?.company_id;

  // ─── Fetch data (server-side filtering + pagination) ───
  const fetchContacts = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    setError(null);

    try {
      let query = supabase
        .from('contacts')
        .select('*', { count: 'exact', head: false })
        .eq('company_id', companyId);

      // Server-side filters
      if (groupFilter) {
        query = query.eq('group_id', groupFilter);
      }
      if (tagFilter) {
        query = query.contains('tags', [tagFilter]);
      }
      if (search) {
        const escaped = search.replace(/'/g, "''").replace(/%/g, '\\%').replace(/_/g, '\\_');
        query = query.or(
          `name.ilike.%${escaped}%,phone.ilike.%${escaped}%,email.ilike.%${escaped}%,company_name.ilike.%${escaped}%`,
        );
      }

      // Server-side pagination
      const from = (page - 1) * PAGE_SIZE;
      const to = from + PAGE_SIZE - 1;
      query = query.order('created_at', { ascending: false }).range(from, to);

      const { data, error: fetchError, count } = await query;

      if (fetchError) {
        setError(fetchError.message);
        setContacts([]);
        setTotalCount(0);
      } else {
        setContacts((data || []) as Contact[]);
        setTotalCount(count ?? 0);
      }
    } catch (err: any) {
      setError(err?.message || 'Erro ao carregar contatos');
      setContacts([]);
      setTotalCount(0);
    }
    setLoading(false);
  }, [companyId, page, search, groupFilter, tagFilter]);

  const fetchGroups = useCallback(async () => {
    if (!companyId) return;
    const { data, error: fetchError } = await supabase
      .from('groups')
      .select('*')
      .eq('company_id', companyId)
      .order('created_at', { ascending: true });

    if (!fetchError && data) {
      setGroups(data as Group[]);
    }
  }, [companyId]);

  // ─── Fetch all contact IDs for "select all" mode (used for bulk operations) ───
  const fetchAllMatchingIds = useCallback(async (): Promise<string[]> => {
    if (!companyId) return [];

    let query = supabase
      .from('contacts')
      .select('id')
      .eq('company_id', companyId);

    if (groupFilter) query = query.eq('group_id', groupFilter);
    if (tagFilter) query = query.contains('tags', [tagFilter]);
    if (search) {
      const escaped = search.replace(/'/g, "''").replace(/%/g, '\\%').replace(/_/g, '\\_');
      query = query.or(
        `name.ilike.%${escaped}%,phone.ilike.%${escaped}%,email.ilike.%${escaped}%,company_name.ilike.%${escaped}%`,
      );
    }

    // Fetch in batches of 1000 to get all matching IDs
    const allIds: string[] = [];
    let from = 0;
    const batchSize = 1000;
    let hasMore = true;

    while (hasMore) {
      const { data, error: fetchError } = await query
        .order('created_at', { ascending: false })
        .range(from, from + batchSize - 1);

      if (fetchError || !data || data.length === 0) {
        hasMore = false;
        break;
      }

      allIds.push(...(data as { id: string }[]).map((c) => c.id));

      if (data.length < batchSize) {
        hasMore = false;
      } else {
        from += batchSize;
      }
    }

    return allIds;
  }, [companyId, search, groupFilter, tagFilter]);

  // Show toast (defined early so redirect effect can use it)
  const showToast = useCallback((type: 'success' | 'error', message: string) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 4000);
  }, []);

  useEffect(() => {
    if (companyId) {
      fetchContacts();
      fetchGroups();
    }
  }, [companyId, fetchContacts, fetchGroups]);

  // Restore Google import filters from sessionStorage when coming back to this page
  useEffect(() => {
    const storedTag = sessionStorage.getItem('contacts_last_import_tag');
    const storedGroup = sessionStorage.getItem('contacts_last_import_group');
    if (storedTag) {
      setTagFilter(storedTag);
      sessionStorage.removeItem('contacts_last_import_tag');
    }
    if (storedGroup) {
      setGroupFilter(storedGroup);
      sessionStorage.removeItem('contacts_last_import_group');
    }
  }, []);

  // Read group from URL query param (e.g. ?group=xxx from Groups page)
  useEffect(() => {
    const groupFromUrl = searchParams.get('group');
    if (groupFromUrl) {
      setGroupFilter(groupFromUrl);
      setPage(1);
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('group');
      setSearchParams(newParams, { replace: true });
    }
  }, []); // run once on mount

  // Handle redirect fallback from Google OAuth callback
  useEffect(() => {
    const googleImport = searchParams.get('googleImport');
    const googleImportError = searchParams.get('googleImportError');

    if (googleImport === '1') {
      const storedToken = sessionStorage.getItem('google_oauth_token');
      const storedTimestamp = sessionStorage.getItem('google_oauth_timestamp');

      const newParams = new URLSearchParams(searchParams);
      newParams.delete('googleImport');
      setSearchParams(newParams, { replace: true });

      if (storedToken && storedTimestamp) {
        const elapsed = Date.now() - parseInt(storedTimestamp, 10);
        if (elapsed < 10 * 60 * 1000) {
          setGoogleModalOpen(true);
          showToast('success', 'Autorização Google concluída! Importando contatos...');
        } else {
          sessionStorage.removeItem('google_oauth_token');
          sessionStorage.removeItem('google_oauth_timestamp');
          showToast('error', 'Token Google expirado. Tente novamente.');
        }
      } else {
        showToast('error', 'Token Google não encontrado. Tente novamente.');
      }
    }

    if (googleImportError === '1') {
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('googleImportError');
      setSearchParams(newParams, { replace: true });
      showToast('error', 'Falha na autorização do Google. Verifique as configurações e tente novamente.');
    }
  }, []); // Run only once on mount

  // ─── Derived data ───
  const groupColorMap = useMemo(
    () => Object.fromEntries(groups.map((g) => [g.id, g.color])),
    [groups],
  );
  const groupNameMap = useMemo(
    () => Object.fromEntries(groups.map((g) => [g.id, g.name])),
    [groups],
  );

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  // contacts is already filtered and paginated from the server
  const paginated = contacts;

  const allPageIds = paginated.map((c) => c.id);
  const allSelected = allPageIds.length > 0 && allPageIds.every((id) => selectedIds.has(id));
  const someSelected = allPageIds.some((id) => selectedIds.has(id)) && !allSelected;

  const toggleAll = () => {
    if (allSelected) {
      const newSet = new Set(selectedIds);
      allPageIds.forEach((id) => newSet.delete(id));
      setSelectedIds(newSet);
      setSelectAllMode(false);
    } else {
      setSelectedIds(new Set([...selectedIds, ...allPageIds]));
    }
  };

  const toggleOne = (id: string) => {
    if (selectAllMode) {
      setSelectAllMode(false);
      const newSet = new Set(paginated.map((c) => c.id));
      newSet.delete(id);
      setSelectedIds(newSet);
      return;
    }
    const newSet = new Set(selectedIds);
    if (newSet.has(id)) newSet.delete(id);
    else newSet.add(id);
    setSelectedIds(newSet);
  };

  // Highlight recently updated row
  const highlightRow = useCallback((id: string) => {
    setRecentlyUpdated((prev) => new Set(prev).add(id));
    setTimeout(() => {
      setRecentlyUpdated((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }, 3000);
  }, []);

  // ─── CRUD ───
  const handleNew = () => {
    setEditingContact(null);
    setModalOpen(true);
  };

  const handleEdit = (contact: Contact) => {
    setEditingContact(contact);
    setModalOpen(true);
  };

  const handleDelete = (contact: Contact) => {
    setDeleteTarget(contact);
    setDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setSaving(true);
    const { error: delError } = await supabase
      .from('contacts')
      .delete()
      .eq('id', deleteTarget.id);

    if (!delError) {
      showToast('success', 'Contato excluído com sucesso!');
    } else {
      showToast('error', 'Erro ao excluir: ' + delError.message);
    }
    setSaving(false);
    setDeleteTarget(null);
    setDeleteModalOpen(false);
    // Re-fetch current page after deletion
    fetchContacts();
  };

  const handleSave = async (data: Partial<Contact>) => {
    if (!companyId) return;
    setSaving(true);

    if (editingContact) {
      const updates: Record<string, unknown> = {
        name: data.name,
        phone: data.phone,
        email: data.email || null,
        company_name: data.company_name || null,
        city: data.city || null,
        state: data.state || null,
        tags: data.tags || [],
        notes: data.notes || null,
        group_id: data.group_id || null,
        updated_at: new Date().toISOString(),
      };
      const { error: updError } = await supabase
        .from('contacts')
        .update(updates)
        .eq('id', editingContact.id);

      if (!updError) {
        highlightRow(editingContact.id);
        showToast('success', 'Contato atualizado com sucesso!');
      } else {
        showToast('error', 'Erro ao atualizar contato: ' + updError.message);
      }
    } else {
      const newContact = {
        company_id: companyId,
        name: data.name || '',
        phone: data.phone || '',
        email: data.email || null,
        company_name: data.company_name || null,
        city: data.city || null,
        state: data.state || null,
        tags: data.tags || [],
        notes: data.notes || null,
        group_id: data.group_id || null,
        created_by: profile?.id || null,
      };
      const { error: insError } = await supabase
        .from('contacts')
        .insert(newContact);

      if (!insError) {
        showToast('success', 'Contato criado com sucesso!');
      } else {
        showToast('error', 'Erro ao criar contato: ' + insError.message);
      }
    }

    setSaving(false);
    setModalOpen(false);
    // Re-fetch current page after save
    fetchContacts();
  };

  // ─── Export ───
  const handleExport = async () => {
    if (selectedIds.size > 0 || selectAllMode) {
      // Export selected — need to fetch full data for all selected IDs
      showToast('success', 'Preparando exportação...');
      const idsToExport = selectAllMode
        ? await fetchAllMatchingIds()
        : Array.from(selectedIds);

      if (idsToExport.length === 0) {
        showToast('error', 'Nenhum contato para exportar.');
        return;
      }

      // Fetch all selected contacts in batches
      const allContacts: Contact[] = [];
      for (let i = 0; i < idsToExport.length; i += 1000) {
        const chunk = idsToExport.slice(i, i + 1000);
        const { data } = await supabase
          .from('contacts')
          .select('*')
          .in('id', chunk);
        if (data) allContacts.push(...(data as Contact[]));
      }

      const headers = ['Nome', 'Telefone', 'Email', 'Empresa', 'Cidade', 'Estado', 'Tags', 'Grupo'];
      const rows = allContacts.map((c) => [
        c.name,
        c.phone,
        c.email || '',
        c.company_name || '',
        c.city || '',
        c.state || '',
        (c.tags || []).join('; '),
        groupNameMap[c.group_id || ''] || '',
      ]);
      const csv = [headers.join(','), ...rows.map((r) => r.map((v) => `"${v}"`).join(','))].join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = 'contatos.csv';
      link.click();
      showToast('success', `${idsToExport.length} contatos exportados!`);
    } else {
      // Export all visible (not filtered) — just export current page for performance
      const headers = ['Nome', 'Telefone', 'Email', 'Empresa', 'Cidade', 'Estado', 'Tags', 'Grupo'];
      const rows = contacts.map((c) => [
        c.name,
        c.phone,
        c.email || '',
        c.company_name || '',
        c.city || '',
        c.state || '',
        (c.tags || []).join('; '),
        groupNameMap[c.group_id || ''] || '',
      ]);
      const csv = [headers.join(','), ...rows.map((r) => r.map((v) => `"${v}"`).join(','))].join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = 'contatos.csv';
      link.click();
      showToast('success', `${contacts.length} contatos exportados na página atual! Para exportar todos, selecione os contatos primeiro.`);
    }
  };

  // ─── Effective selected count (respects selectAllMode) ───
  const effectiveSelectedCount = selectAllMode ? totalCount : selectedIds.size;

  // ─── Delete in batches of 100 ───
  const deleteInBatches = async (ids: string[], label: string) => {
    const BATCH_SIZE = 100;
    let failed = 0;

    setDeleteProgress({ current: 0, total: ids.length });

    for (let i = 0; i < ids.length; i += BATCH_SIZE) {
      const chunk = ids.slice(i, i + BATCH_SIZE);
      const { error: batchErr } = await supabase
        .from('contacts')
        .delete()
        .in('id', chunk);

      if (batchErr) {
        failed += chunk.length;
      }

      setDeleteProgress({ current: Math.min(i + BATCH_SIZE, ids.length), total: ids.length });
    }

    setDeleteProgress(null);
    return { deleted: ids.length - failed, failed };
  };

  const handleDeleteSelected = async () => {
    if (!selectAllMode && selectedIds.size === 0) return;

    setSaving(true);

    let ids: string[];
    if (selectAllMode) {
      ids = await fetchAllMatchingIds();
    } else {
      ids = Array.from(selectedIds);
    }

    if (ids.length === 0) {
      setSaving(false);
      return;
    }

    const { deleted, failed } = await deleteInBatches(ids, 'selecionados');
    setSaving(false);
    setSelectedIds(new Set());
    setSelectAllMode(false);
    setDeleteAllConfirmOpen(false);
    setDeleteAllTarget(null);

    if (failed > 0) {
      showToast('error', `${deleted} contatos excluídos, mas ${failed} falharam.`);
    } else {
      showToast('success', `${deleted} contato${deleted !== 1 ? 's' : ''} excluído${deleted !== 1 ? 's' : ''} com sucesso!`);
    }
    fetchContacts();
  };

  const handleDeleteAll = async () => {
    if (totalCount === 0) return;

    setSaving(true);
    const allIds = await fetchAllMatchingIds();

    if (allIds.length === 0) {
      setSaving(false);
      return;
    }

    const { deleted, failed } = await deleteInBatches(allIds, 'todos');
    setSaving(false);
    setSelectedIds(new Set());
    setSelectAllMode(false);
    setDeleteAllConfirmOpen(false);
    setDeleteAllTarget(null);

    if (failed > 0) {
      showToast('error', `${deleted} contatos excluídos, mas ${failed} falharam.`);
    } else {
      showToast('success', `Todos os ${deleted} contato${deleted !== 1 ? 's' : ''} foram excluídos!`);
    }
    fetchContacts();
  };

  const handleBulkDelete = async () => {
    if (effectiveSelectedCount === 0) return;
    setDeleteAllTarget('selected');
    setDeleteAllConfirmOpen(true);
  };

  // ─── Bulk edit in batches of 100 ───
  const handleBulkEditConfirm = async (changes: {
    group_id: string | null;
    addTags: string[];
    removeTags: string[];
    replaceTags: string[] | null;
    state: string | null;
  }) => {
    if (!selectAllMode && selectedIds.size === 0) return;

    setSaving(true);
    setBulkEditProgress({ current: 0, total: effectiveSelectedCount });

    let ids: string[];
    if (selectAllMode) {
      ids = await fetchAllMatchingIds();
    } else {
      ids = Array.from(selectedIds);
    }

    if (ids.length === 0) {
      setSaving(false);
      setBulkEditProgress(null);
      return;
    }

    const BATCH_SIZE = 100;
    let updated = 0;
    let failed = 0;

    for (let i = 0; i < ids.length; i += BATCH_SIZE) {
      const chunkIds = ids.slice(i, i + BATCH_SIZE);

      const { data: chunkContacts, error: fetchErr } = await supabase
        .from('contacts')
        .select('id, tags, state')
        .in('id', chunkIds);

      if (fetchErr || !chunkContacts) {
        failed += chunkIds.length;
        setBulkEditProgress({ current: Math.min(i + BATCH_SIZE, ids.length), total: ids.length });
        continue;
      }

      const updates = chunkContacts.map((c: { id: string; tags: string[]; state: string }) => {
        let newTags = c.tags || [];
        if (changes.replaceTags !== null) {
          newTags = [...changes.replaceTags];
        } else {
          if (changes.addTags.length > 0) {
            newTags = [...new Set([...(c.tags || []), ...changes.addTags])];
          }
          if (changes.removeTags.length > 0) {
            const removeSet = new Set(changes.removeTags.map((t) => t.toLowerCase()));
            newTags = (c.tags || []).filter((t: string) => !removeSet.has(t.toLowerCase()));
          }
        }

        const update: Record<string, unknown> = {
          updated_at: new Date().toISOString(),
          tags: newTags,
        };
        if (changes.group_id !== null) {
          update.group_id = changes.group_id === '__none__' ? null : changes.group_id;
        }
        if (changes.state !== null) {
          update.state = changes.state;
        }

        return { id: c.id, ...update };
      });

      for (const u of updates) {
        const { error: updErr } = await supabase
          .from('contacts')
          .update({
            group_id: u.group_id,
            tags: u.tags,
            state: u.state,
            updated_at: u.updated_at,
          })
          .eq('id', u.id);

        if (updErr) {
          failed += 1;
        } else {
          updated += 1;
        }
      }

      setBulkEditProgress({ current: Math.min(i + BATCH_SIZE, ids.length), total: ids.length });
    }

    setSaving(false);
    setBulkEditProgress(null);
    setBulkEditOpen(false);
    setSelectedIds(new Set());
    setSelectAllMode(false);

    if (failed > 0) {
      showToast('error', `${updated} contatos atualizados, mas ${failed} falharam.`);
    } else {
      showToast('success', `${updated} contato${updated !== 1 ? 's' : ''} atualizado${updated !== 1 ? 's' : ''} com sucesso!`);
    }
    fetchContacts();
  };

  // ─── Bulk add Brazilian DDD ───
  const handleBulkAddCountryCode = async (selectedDdd: string) => {
    if (!selectAllMode && selectedIds.size === 0) return;
    if (!selectedDdd) return;

    setSaving(true);

    let ids: string[];
    if (selectAllMode) {
      ids = await fetchAllMatchingIds();
    } else {
      ids = Array.from(selectedIds);
    }

    if (ids.length === 0) {
      setSaving(false);
      return;
    }

    setBulkEditProgress({ current: 0, total: ids.length });

    const BATCH_SIZE = 100;
    let updated = 0;
    let failed = 0;
    let skipped = 0;

    for (let i = 0; i < ids.length; i += BATCH_SIZE) {
      const chunkIds = ids.slice(i, i + BATCH_SIZE);

      const { data: chunkContacts, error: fetchErr } = await supabase
        .from('contacts')
        .select('id, phone')
        .in('id', chunkIds);

      if (fetchErr || !chunkContacts) {
        failed += chunkIds.length;
        setBulkEditProgress({ current: Math.min(i + BATCH_SIZE, ids.length), total: ids.length });
        continue;
      }

      for (const c of chunkContacts as { id: string; phone: string }[]) {
        const normalized = normalizeBrazilianPhone(c.phone, selectedDdd);
        const digits = c.phone.replace(/\D/g, '');

        // Skip if already correct
        if (normalized === digits) {
          skipped += 1;
          continue;
        }

        const { error: updErr } = await supabase
          .from('contacts')
          .update({ phone: normalized, updated_at: new Date().toISOString() })
          .eq('id', c.id);

        if (updErr) {
          failed += 1;
        } else {
          updated += 1;
        }
      }

      setBulkEditProgress({ current: Math.min(i + BATCH_SIZE, ids.length), total: ids.length });
    }

    setSaving(false);
    setBulkEditProgress(null);
    setBulkCountryCodeOpen(false);
    setSelectedIds(new Set());
    setSelectAllMode(false);

    const parts: string[] = [];
    if (updated > 0) parts.push(`${updated} contato${updated !== 1 ? 's' : ''} formatado${updated !== 1 ? 's' : ''} com 55 + DDD ${selectedDdd}`);
    if (skipped > 0) parts.push(`${skipped} já estavam corretos`);
    if (failed > 0) parts.push(`${failed} falharam`);

    if (failed > 0) {
      showToast('error', parts.join(' · '));
    } else {
      showToast('success', parts.join(' · '));
    }
    fetchContacts();
  };

  // ─── Fix all country codes via Edge Function ───
  const handleFixAllCountryCode = async (selectedDdd: string) => {
    if (!companyId || !selectedDdd) return;

    setFixAllProgress(true);
    setFixAllResult(null);

    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token || '';

      const response = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/fix-country-code`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            ddd: selectedDdd,
            company_id: companyId,
            group_id: groupFilter || undefined,
          }),
        },
      );

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.error || 'Erro ao processar');
      }

      setFixAllResult({
        total: result.total || 0,
        updated: result.updated || 0,
        skipped: result.skipped || 0,
        errored: result.errored || 0,
      });

      showToast('success', result.message || `${result.updated} contatos formatados com 55 + DDD ${selectedDdd}`);
      fetchContacts();
    } catch (err: any) {
      showToast('error', err?.message || 'Erro ao corrigir DDD');
    } finally {
      setFixAllProgress(false);
    }
  };

  // ─── Import handler ───
  const handleImport = async (newContacts: Contact[]) => {
    if (!companyId || newContacts.length === 0) return;

    const BATCH_SIZE = 50;
    const now = new Date().toISOString();
    const rows = newContacts.map((c) => ({
      company_id: companyId,
      name: c.name,
      phone: c.phone,
      email: c.email || null,
      company_name: c.company_name || null,
      city: c.city || null,
      state: c.state || null,
      tags: c.tags || [],
      notes: c.notes || null,
      group_id: c.group_id || null,
      created_by: profile?.id || null,
      created_at: now,
    }));

    setImportProgress({ current: 0, total: rows.length });

    let inserted = 0;
    let failed = 0;

    for (let i = 0; i < rows.length; i += BATCH_SIZE) {
      const chunk = rows.slice(i, i + BATCH_SIZE);

      try {
        const { data, error: chunkErr } = await supabase.from('contacts').insert(chunk).select();

        if (chunkErr) {
          failed += chunk.length;
          console.error('Import batch error:', chunkErr.message);
        } else if (data) {
          inserted += data.length;
        } else {
          inserted += chunk.length;
        }
      } catch (err: any) {
        failed += chunk.length;
        console.error('Import batch exception:', err?.message || err);
      }

      setImportProgress({ current: Math.min(i + BATCH_SIZE, rows.length), total: rows.length });
    }

    setImportProgress(null);

    // Determine group name for toast message
    const targetGroupId = newContacts[0]?.group_id;
    const targetGroupName = targetGroupId ? groups.find((g) => g.id === targetGroupId)?.name : null;

    // Auto-filter to show only the newly imported contacts
    setPage(1);
    setSearch('');
    setTagFilter('google-import');
    setGroupFilter(targetGroupId || '');
    // Persist filter in sessionStorage so it survives page navigation
    sessionStorage.setItem('contacts_last_import_group', targetGroupId || '');
    sessionStorage.setItem('contacts_last_import_tag', 'google-import');

    if (failed > 0) {
      if (inserted > 0) {
        if (targetGroupName) {
          showToast('error', `${inserted} contatos importados no grupo "${targetGroupName}", mas ${failed} falharam.`);
        } else {
          showToast('error', `${inserted} contatos importados, mas ${failed} falharam.`);
        }
      } else {
        const msg = 'Nenhum contato foi importado. Verifique se os dados estão corretos.';
        showToast('error', msg);
        throw new Error(msg);
      }
    } else {
      if (targetGroupName) {
        showToast('success', `${inserted} contato${inserted !== 1 ? 's' : ''} importado${inserted !== 1 ? 's' : ''} no grupo "${targetGroupName}" com sucesso!`);
      } else {
        showToast('success', `${inserted} contato${inserted !== 1 ? 's' : ''} importado${inserted !== 1 ? 's' : ''} com sucesso!`);
      }
    }
  };

  if (loading) {
    return (
      <div className="space-y-5">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <div className="h-7 w-28 bg-background-200 rounded animate-pulse" />
            <div className="h-4 w-48 bg-background-100 rounded mt-1 animate-pulse" />
          </div>
        </div>
        <div className="bg-white rounded-xl border border-background-200 p-12 flex items-center justify-center">
          <div className="flex items-center gap-3">
            <div className="w-5 h-5 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
            <span className="text-sm text-foreground-500">Carregando contatos...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-5">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h2 className="text-xl font-heading font-bold text-foreground-950">Contatos</h2>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-background-200 p-12 text-center">
          <div className="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-4">
            <i className="ri-error-warning-line text-2xl text-red-500" />
          </div>
          <p className="text-sm text-foreground-700 font-medium mb-1">Erro ao carregar contatos</p>
          <p className="text-xs text-foreground-500 mb-4">{error}</p>
          <button
            onClick={fetchContacts}
            className="px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white text-sm font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap"
          >
            Tentar novamente
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Toast */}
      {toast && (
        <div className={`rounded-xl px-4 py-3 flex items-center gap-3 animate-fadeIn ${
          toast.type === 'success'
            ? 'bg-emerald-50 border border-emerald-200 text-emerald-700'
            : 'bg-red-50 border border-red-200 text-red-700'
        }`}>
          <i className={`text-lg ${toast.type === 'success' ? 'ri-check-line text-emerald-500' : 'ri-error-warning-line text-red-500'}`} />
          <p className="text-sm font-medium">{toast.message}</p>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-heading font-bold text-foreground-950">Contatos</h2>
          <p className="text-sm text-foreground-500 mt-1">Gerencie sua lista de contatos para campanhas</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Link
            to="/contacts/import-tutorial"
            className="px-3 py-2 bg-white border border-background-200 hover:bg-background-50 text-foreground-600 text-sm font-medium rounded-lg transition-colors flex items-center gap-2 whitespace-nowrap cursor-pointer"
            title="Ver tutorial de importação"
          >
            <i className="ri-question-line text-lg" />
            Precisa de ajuda?
          </Link>
          <button
            onClick={handleNew}
            disabled={saving}
            className="px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white text-sm font-semibold rounded-lg transition-colors flex items-center gap-2 whitespace-nowrap cursor-pointer disabled:opacity-50"
          >
            <i className="ri-add-line text-lg" />
            Novo contato
          </button>
          <button
            onClick={() => setImportModalOpen(true)}
            className="px-4 py-2 bg-white border border-background-200 hover:bg-background-50 text-foreground-700 text-sm font-medium rounded-lg transition-colors flex items-center gap-2 whitespace-nowrap cursor-pointer"
          >
            <i className="ri-upload-line text-lg" />
            Importar
          </button>
          <button
            onClick={handleExport}
            className="px-4 py-2 bg-white border border-background-200 hover:bg-background-50 text-foreground-700 text-sm font-medium rounded-lg transition-colors flex items-center gap-2 whitespace-nowrap cursor-pointer"
          >
            <i className="ri-download-line text-lg" />
            Exportar
          </button>
        </div>
      </div>

      {/* Google Contacts banner */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 p-4 rounded-xl bg-background-50 border border-background-200">
        <div className="flex items-center gap-3 flex-1">
          <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center flex-shrink-0 border border-background-200 shadow-sm">
            <i className="ri-google-line text-xl text-foreground-600" />
          </div>
          <div>
            <p className="text-sm font-semibold text-foreground-900">Importar contatos do Google</p>
            <p className="text-xs text-foreground-500">Conecte sua conta Google e importe seus contatos em segundos, sem precisar exportar CSV.</p>
          </div>
        </div>
        <button
          onClick={() => setGoogleModalOpen(true)}
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-white hover:bg-background-100 border border-background-200 text-foreground-700 text-sm font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap flex-shrink-0"
        >
          <i className="ri-google-line text-lg" />
          Conectar Google
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
        <div className="relative flex-1">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            placeholder="Buscar por nome, telefone, email ou empresa..."
            className="w-full pl-9 pr-4 py-2 bg-white border border-background-200 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
          />
        </div>
        <select
          value={groupFilter}
          onChange={(e) => { setGroupFilter(e.target.value); setPage(1); }}
          className="px-3 py-2 bg-white border border-background-200 rounded-lg text-sm text-foreground-900 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
        >
          <option value="">Todos os grupos</option>
          {groups.map((g) => (
            <option key={g.id} value={g.id}>{g.name}</option>
          ))}
        </select>
        <button
          onClick={() => {
            setTagFilter(tagFilter === 'google-import' ? '' : 'google-import');
            setPage(1);
            setGroupFilter('');
            setSearch('');
          }}
          className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-1.5 whitespace-nowrap cursor-pointer border ${
            tagFilter === 'google-import'
              ? 'bg-primary-50 border-primary-300 text-primary-700'
              : 'bg-white border-background-200 text-foreground-600 hover:bg-background-50'
          }`}
        >
          <i className="ri-google-line text-base" />
          Google
          {tagFilter === 'google-import' && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                setTagFilter('');
                setPage(1);
              }}
              className="ml-0.5 w-4 h-4 flex items-center justify-center rounded-full hover:bg-primary-200 transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-xs" />
            </button>
          )}
        </button>
        <div className="relative">
          <i className="ri-price-tag-3-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400" />
          <input
            type="text"
            value={tagFilter}
            onChange={(e) => { setTagFilter(e.target.value); setPage(1); }}
            placeholder="Filtrar por tag..."
            className="pl-9 pr-3 py-2 bg-white border border-background-200 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
          />
        </div>
      </div>

      {/* Group stats banner — shows when a group is filtered */}
      {groupFilter && totalCount > 0 && (
        <div className="flex items-center gap-3 p-4 rounded-xl bg-primary-50 border border-primary-200 animate-fadeIn">
          <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center flex-shrink-0 border border-primary-200 shadow-sm">
            <i className="ri-group-line text-xl text-primary-600" />
          </div>
          <div className="flex-1">
            <p className="text-sm font-semibold text-primary-800">
              {totalCount} contato{totalCount !== 1 ? 's' : ''} no grupo <span className="text-primary-600">"{groupNameMap[groupFilter] || groupFilter}"</span>
            </p>
            <p className="text-xs text-primary-600">
              Mostrando página {page} de {totalPages}
            </p>
          </div>
          <button
            onClick={() => { setGroupFilter(''); setPage(1); }}
            className="px-3 py-1.5 bg-white hover:bg-primary-100 border border-primary-200 text-primary-700 text-xs font-medium rounded-lg transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1.5"
          >
            <i className="ri-close-line" />
            Limpar filtro
          </button>
        </div>
      )}

      {/* Google import stats banner — shows when google tag is filtered */}
      {tagFilter === 'google-import' && !groupFilter && totalCount > 0 && (
        <div className="flex items-center gap-3 p-4 rounded-xl bg-emerald-50 border border-emerald-200 animate-fadeIn">
          <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center flex-shrink-0 border border-emerald-200 shadow-sm">
            <i className="ri-google-line text-xl text-emerald-600" />
          </div>
          <div className="flex-1">
            <p className="text-sm font-semibold text-emerald-800">
              {totalCount} contato{totalCount !== 1 ? 's' : ''} importado{totalCount !== 1 ? 's' : ''} do Google
            </p>
            <p className="text-xs text-emerald-600">
              Mostrando página {page} de {totalPages}
            </p>
          </div>
          <button
            onClick={() => { setTagFilter(''); setPage(1); }}
            className="px-3 py-1.5 bg-white hover:bg-emerald-100 border border-emerald-200 text-emerald-700 text-xs font-medium rounded-lg transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1.5"
          >
            <i className="ri-close-line" />
            Limpar filtro
          </button>
        </div>
      )}

      {/* Bulk toolbar */}
      {(selectedIds.size > 0 || selectAllMode) && (
        <div className="flex items-center justify-between bg-primary-50 border border-primary-200 rounded-lg px-4 py-2.5 animate-fadeIn">
          <p className="text-sm text-primary-700 font-medium">
            {selectAllMode ? (
              <>Todos os <strong>{totalCount}</strong> contatos selecionados</>
            ) : (
              <>{selectedIds.size} contato{selectedIds.size > 1 ? 's' : ''} selecionado{selectedIds.size > 1 ? 's' : ''}{' '}
                {totalCount > selectedIds.size && (
                  <button
                    onClick={() => setSelectAllMode(true)}
                    className="text-primary-600 hover:text-primary-800 underline cursor-pointer font-medium"
                  >
                    Selecionar todos os {totalCount} contatos
                  </button>
                )}
              </>
            )}
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setBulkEditOpen(true)}
              disabled={saving}
              className="px-3 py-1.5 text-sm text-primary-700 hover:bg-primary-100 rounded-md transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1.5"
            >
              <i className="ri-edit-line" />
              Editar em lote
            </button>
            <button
              onClick={() => setBulkCountryCodeOpen(true)}
              disabled={saving}
              className="px-3 py-1.5 text-sm text-primary-700 hover:bg-primary-100 rounded-md transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1.5"
            >
              <i className="ri-global-line" />
              Adicionar DDD 55
            </button>
            <button
              onClick={handleExport}
              className="px-3 py-1.5 text-sm text-primary-700 hover:bg-primary-100 rounded-md transition-colors cursor-pointer whitespace-nowrap"
            >
              Exportar selecionados
            </button>
            <button
              onClick={handleBulkDelete}
              disabled={saving}
              className="px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 rounded-md transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
            >
              <i className="ri-delete-bin-line mr-1" />
              Excluir {effectiveSelectedCount > 0 ? effectiveSelectedCount : ''}
            </button>
            <button
              onClick={() => { setSelectedIds(new Set()); setSelectAllMode(false); }}
              className="px-3 py-1.5 text-sm text-foreground-500 hover:bg-background-100 rounded-md transition-colors cursor-pointer whitespace-nowrap"
            >
              Limpar
            </button>
          </div>
        </div>
      )}

      {/* Bulk delete progress bar */}
      {deleteProgress && (
        <div className="rounded-xl bg-white border border-background-200 p-4 animate-fadeIn">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-medium text-foreground-700">
              Excluindo contatos...
            </p>
            <p className="text-xs text-foreground-500 tabular-nums">
              {deleteProgress.current} / {deleteProgress.total}
            </p>
          </div>
          <div className="w-full h-2 rounded-full bg-background-200 overflow-hidden">
            <div
              className="h-full bg-red-500 rounded-full transition-all duration-300 ease-out"
              style={{ width: `${deleteProgress.total > 0 ? (deleteProgress.current / deleteProgress.total) * 100 : 0}%` }}
            />
          </div>
        </div>
      )}

      {/* Bulk edit progress bar */}
      {bulkEditProgress && (
        <div className="rounded-xl bg-white border border-background-200 p-4 animate-fadeIn">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-medium text-foreground-700">
              Atualizando contatos...
            </p>
            <p className="text-xs text-foreground-500 tabular-nums">
              {bulkEditProgress.current} / {bulkEditProgress.total}
            </p>
          </div>
          <div className="w-full h-2 rounded-full bg-background-200 overflow-hidden">
            <div
              className="h-full bg-primary-500 rounded-full transition-all duration-300 ease-out"
              style={{ width: `${bulkEditProgress.total > 0 ? (bulkEditProgress.current / bulkEditProgress.total) * 100 : 0}%` }}
            />
          </div>
        </div>
      )}

      {/* Import progress bar */}
      {importProgress && (
        <div className="rounded-xl bg-white border border-emerald-200 p-4 animate-fadeIn">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-medium text-foreground-700 flex items-center gap-2">
              <i className="ri-download-line text-emerald-500" />
              Importando contatos...
            </p>
            <p className="text-xs text-foreground-500 tabular-nums">
              {importProgress.current} / {importProgress.total}
            </p>
          </div>
          <div className="w-full h-2 rounded-full bg-background-200 overflow-hidden">
            <div
              className="h-full bg-emerald-500 rounded-full transition-all duration-300 ease-out"
              style={{ width: `${importProgress.total > 0 ? (importProgress.current / importProgress.total) * 100 : 0}%` }}
            />
          </div>
        </div>
      )}

      {/* Delete all bar — always visible with quick action */}
      {!selectAllMode && selectedIds.size === 0 && contacts.length > 0 && (
        <div className="flex items-center justify-between px-4 py-2.5 rounded-lg bg-background-50 border border-background-200">
          <div className="flex items-center gap-2">
            <i className="ri-delete-bin-line text-foreground-400 text-sm" />
            <p className="text-xs text-foreground-500">
              {totalCount > 0 ? (
                <>{totalCount} contato{totalCount !== 1 ? 's' : ''} no total{groupFilter || tagFilter || search ? ' com filtros atuais' : ''}</>
              ) : (
                <>Nenhum contato encontrado com os filtros atuais</>
              )}
            </p>
          </div>
          {totalCount > 0 && (
            <div className="flex items-center gap-2">
              <button
                onClick={() => setFixAllCountryCodeOpen(true)}
                className="px-3 py-1.5 text-xs font-medium text-primary-600 hover:bg-primary-50 rounded-md transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1.5"
                title="Corrige código de país +54 em TODOS os contatos de uma vez (via servidor)"
              >
                <i className="ri-global-line" />
                Corrigir +54 em TODOS
              </button>
              <button
                onClick={() => { setDeleteAllTarget('all'); setDeleteAllConfirmOpen(true); }}
                className="px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50 rounded-md transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-delete-bin-line mr-1" />
                Excluir TODOS
              </button>
            </div>
          )}
        </div>
      )}

      {/* Empty state when no results */}
      {contacts.length === 0 && !loading && (
        <div className="bg-white rounded-xl border border-background-200 p-12 text-center">
          <i className="ri-user-search-line text-4xl text-foreground-300 mb-3 block" />
          <p className="text-sm text-foreground-500">Nenhum contato encontrado</p>
          <p className="text-xs text-foreground-400 mt-1">
            {groupFilter || tagFilter || search
              ? 'Tente ajustar os filtros ou limpar a busca'
              : 'Comece importando contatos ou criando um novo'}
          </p>
          {(groupFilter || tagFilter || search) && (
            <button
              onClick={() => { setGroupFilter(''); setTagFilter(''); setSearch(''); setPage(1); }}
              className="mt-3 px-4 py-2 bg-background-100 hover:bg-background-200 text-foreground-600 text-sm font-medium rounded-lg transition-colors cursor-pointer whitespace-nowrap"
            >
              Limpar todos os filtros
            </button>
          )}
        </div>
      )}

      {/* Table */}
      {contacts.length > 0 && (
        <div className="bg-white rounded-xl border border-background-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-background-100">
                  <th className="px-5 py-3 text-left">
                    <input
                      type="checkbox"
                      checked={allSelected}
                      ref={(el) => { if (el) el.indeterminate = someSelected; }}
                      onChange={toggleAll}
                      className="w-4 h-4 rounded border-background-300 text-primary-500 focus:ring-primary-400"
                    />
                  </th>
                  <th className="px-5 py-3 text-left text-xs font-semibold text-foreground-500 uppercase tracking-wider">Nome</th>
                  <th className="px-5 py-3 text-left text-xs font-semibold text-foreground-500 uppercase tracking-wider">Telefone</th>
                  <th className="px-5 py-3 text-left text-xs font-semibold text-foreground-500 uppercase tracking-wider hidden md:table-cell">Email</th>
                  <th className="px-5 py-3 text-left text-xs font-semibold text-foreground-500 uppercase tracking-wider hidden sm:table-cell">Empresa</th>
                  <th className="px-5 py-3 text-left text-xs font-semibold text-foreground-500 uppercase tracking-wider">Grupo</th>
                  <th className="px-5 py-3 text-left text-xs font-semibold text-foreground-500 uppercase tracking-wider hidden lg:table-cell">Tags</th>
                  <th className="px-5 py-3 text-left text-xs font-semibold text-foreground-500 uppercase tracking-wider hidden sm:table-cell">Localização</th>
                  <th className="px-5 py-3 text-right text-xs font-semibold text-foreground-500 uppercase tracking-wider">Ações</th>
                </tr>
              </thead>
              <tbody>
                {paginated.map((contact) => (
                  <tr
                    key={contact.id}
                    className={`border-b border-background-50 hover:bg-background-50/60 transition-colors ${
                      recentlyUpdated.has(contact.id) ? 'bg-amber-50/60' : ''
                    }`}
                  >
                    <td className="px-5 py-3.5">
                      <input
                        type="checkbox"
                        checked={selectedIds.has(contact.id)}
                        onChange={() => toggleOne(contact.id)}
                        className="w-4 h-4 rounded border-background-300 text-primary-500 focus:ring-primary-400"
                      />
                    </td>
                    <td className="px-5 py-3.5">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center text-xs font-semibold text-primary-700 flex-shrink-0">
                          {contact.name.split(' ').map((n) => n[0]).slice(0, 2).join('').toUpperCase()}
                        </div>
                        <span className="font-medium text-foreground-900">{contact.name}</span>
                      </div>
                    </td>
                    <td className="px-5 py-3.5 text-foreground-700 tabular-nums">{contact.phone}</td>
                    <td className="px-5 py-3.5 text-foreground-600 hidden md:table-cell">{contact.email || '—'}</td>
                    <td className="px-5 py-3.5 text-foreground-600 hidden sm:table-cell">{contact.company_name || '—'}</td>
                    <td className="px-5 py-3.5">
                      {contact.group_id ? (
                        <span
                          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium"
                          style={{
                            backgroundColor: `${groupColorMap[contact.group_id] || '#6B7280'}15`,
                            color: groupColorMap[contact.group_id] || '#6B7280',
                          }}
                        >
                          <span
                            className="w-1.5 h-1.5 rounded-full"
                            style={{ backgroundColor: groupColorMap[contact.group_id] || '#6B7280' }}
                          />
                          {groupNameMap[contact.group_id] || '—'}
                        </span>
                      ) : (
                        <span className="text-foreground-400">—</span>
                      )}
                    </td>
                    <td className="px-5 py-3.5 hidden lg:table-cell">
                      <div className="flex flex-wrap gap-1">
                        {(contact.tags || []).slice(0, 3).map((tag) => (
                          <span
                            key={tag}
                            className="inline-block px-2 py-0.5 bg-background-100 text-foreground-600 rounded-md text-xs"
                          >
                            {tag}
                          </span>
                        ))}
                        {(contact.tags || []).length > 3 && (
                          <span className="text-xs text-foreground-400">+{(contact.tags || []).length - 3}</span>
                        )}
                      </div>
                    </td>
                    <td className="px-5 py-3.5 text-foreground-500 hidden sm:table-cell text-xs">
                      {contact.city ? `${contact.city}, ${contact.state}` : '—'}
                    </td>
                    <td className="px-5 py-3.5 text-right">
                      <ActionMenu
                        actions={[
                          {
                            label: 'Editar',
                            icon: 'ri-edit-line',
                            iconColor: 'text-primary-500',
                            onClick: () => handleEdit(contact),
                          },
                          {
                            label: 'Excluir',
                            icon: 'ri-delete-bin-line',
                            danger: true,
                            onClick: () => handleDelete(contact),
                          },
                        ]}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination + Stats */}
          <div className="flex items-center justify-between px-5 py-3 border-t border-background-100">
            <p className="text-xs text-foreground-500">
              {totalCount} resultado{totalCount !== 1 ? 's' : ''} · Página {page} de {totalPages}
            </p>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setPage(Math.max(1, page - 1))}
                disabled={page === 1}
                className="w-8 h-8 flex items-center justify-center rounded-lg text-foreground-600 hover:bg-background-100 disabled:opacity-30 transition-colors cursor-pointer"
              >
                <i className="ri-arrow-left-s-line text-lg" />
              </button>
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => i + Math.max(1, Math.min(page - 2, totalPages - 4))).map((p) => (
                <button
                  key={p}
                  onClick={() => setPage(p)}
                  className={`w-8 h-8 flex items-center justify-center rounded-lg text-xs font-semibold transition-colors cursor-pointer ${
                    p === page ? 'bg-primary-500 text-white' : 'text-foreground-600 hover:bg-background-100'
                  }`}
                >
                  {p}
                </button>
              ))}
              {totalPages > 5 && page < totalPages - 2 && (
                <>
                  <span className="text-foreground-400 text-xs px-1">...</span>
                  <button
                    onClick={() => setPage(totalPages)}
                    className="w-8 h-8 flex items-center justify-center rounded-lg text-xs font-semibold text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer"
                  >
                    {totalPages}
                  </button>
                </>
              )}
              <button
                onClick={() => setPage(Math.min(totalPages, page + 1))}
                disabled={page === totalPages}
                className="w-8 h-8 flex items-center justify-center rounded-lg text-foreground-600 hover:bg-background-100 disabled:opacity-30 transition-colors cursor-pointer"
              >
                <i className="ri-arrow-right-s-line text-lg" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modals */}
      <ContactModal
        contact={editingContact}
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onSave={handleSave}
        groups={groups}
        saving={saving}
      />

      <DeleteModal
        isOpen={deleteModalOpen}
        onClose={() => { setDeleteModalOpen(false); setDeleteTarget(null); }}
        onConfirm={confirmDelete}
        title="Excluir contato"
        message={`Tem certeza que deseja excluir "${deleteTarget?.name || ''}"? Essa ação não pode ser desfeita.`}
      />

      {/* Bulk delete confirmation modal */}
      {deleteAllConfirmOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => { setDeleteAllConfirmOpen(false); setDeleteAllTarget(null); }} />
          <div className="relative bg-white rounded-xl border border-background-200 w-full max-w-[440px] mx-4 p-6 animate-fadeIn">
            <div className="w-12 h-12 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-4">
              <i className="ri-alert-line text-red-500 text-2xl" />
            </div>
            <h3 className="text-lg font-heading font-semibold text-foreground-950 text-center mb-2">
              {deleteAllTarget === 'all' ? 'Excluir TODOS os contatos?' : 'Excluir contatos selecionados?'}
            </h3>
            <p className="text-sm text-foreground-500 text-center mb-2">
              {deleteAllTarget === 'all'
                ? `Você está prestes a excluir permanentemente todos os ${totalCount} contato${totalCount !== 1 ? 's' : ''}${groupFilter || tagFilter || search ? ' com os filtros atuais' : ''} da sua lista.`
                : `Você está prestes a excluir permanentemente ${effectiveSelectedCount} contato${effectiveSelectedCount !== 1 ? 's' : ''} da sua lista.`
              }
            </p>
            <p className="text-xs text-red-500 text-center mb-6 font-medium">
              Esta ação é irreversível! Os contatos serão removidos em lotes de 100.
            </p>
            <div className="flex items-center justify-center gap-3">
              <button
                onClick={() => { setDeleteAllConfirmOpen(false); setDeleteAllTarget(null); }}
                className="px-5 py-2 bg-white border border-background-200 hover:bg-background-50 text-foreground-700 text-sm font-medium rounded-lg transition-colors cursor-pointer whitespace-nowrap"
              >
                Cancelar
              </button>
              <button
                onClick={deleteAllTarget === 'all' ? handleDeleteAll : handleDeleteSelected}
                disabled={saving}
                className="px-5 py-2 bg-red-500 hover:bg-red-600 disabled:opacity-50 text-white text-sm font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2"
              >
                {saving ? (
                  <>
                    <i className="ri-loader-4-line animate-spin" />
                    Excluindo...
                  </>
                ) : (
                  <>
                    <i className="ri-delete-bin-line" />
                    Sim, excluir {deleteAllTarget === 'all' ? 'TODOS' : effectiveSelectedCount}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      <ImportModal
        isOpen={importModalOpen}
        onClose={() => setImportModalOpen(false)}
        onImport={handleImport}
        existingContacts={contacts}
        groups={groups}
      />

      <GoogleContactsModal
        isOpen={googleModalOpen}
        onClose={() => setGoogleModalOpen(false)}
        onImport={handleImport}
        existingContacts={contacts}
        groups={groups}
      />

      {/* Bulk country code modal */}
      {bulkCountryCodeOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => setBulkCountryCodeOpen(false)} />
          <div className="relative bg-white rounded-xl border border-background-200 w-full max-w-[400px] mx-4 p-6 animate-fadeIn">
            <div className="w-12 h-12 rounded-full bg-primary-50 flex items-center justify-center mx-auto mb-4">
              <i className="ri-global-line text-primary-600 text-2xl" />
            </div>
            <h3 className="text-lg font-heading font-semibold text-foreground-950 text-center mb-2">
              Formatar telefones (55 + DDD)
            </h3>
            <p className="text-sm text-foreground-500 text-center mb-6">
              Formata os telefones dos <strong>{effectiveSelectedCount}</strong> contato{effectiveSelectedCount !== 1 ? 's' : ''} selecionado{effectiveSelectedCount !== 1 ? 's' : ''} no padrão brasileiro.
              Números sem DDD recebem <strong>55 + DDD</strong>, números com DDD mas sem 55 recebem <strong>55</strong>.
              Contatos já corretos (12-13 dígitos) são ignorados.
            </p>

            <div className="space-y-3 mb-6">
              <label className="text-sm font-medium text-foreground-700">DDD (Código de Área)</label>
              <select
                id="bulk-country-code"
                className="w-full px-4 py-3 bg-white border-2 border-background-200 rounded-xl text-sm text-foreground-900 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all cursor-pointer font-medium"
                defaultValue={DEFAULT_DDD}
              >
                {BRAZIL_DDDS.map((d) => (
                  <option key={d.code} value={d.code}>{d.code} — {d.state} ({d.region})</option>
                ))}
              </select>
            </div>

            <div className="flex items-center justify-center gap-3">
              <button
                onClick={() => setBulkCountryCodeOpen(false)}
                className="px-5 py-2 bg-white border border-background-200 hover:bg-background-50 text-foreground-700 text-sm font-medium rounded-lg transition-colors cursor-pointer whitespace-nowrap"
              >
                Cancelar
              </button>
              <button
                onClick={() => {
                  const select = document.getElementById('bulk-country-code') as HTMLSelectElement;
                  handleBulkAddCountryCode(select?.value || '54');
                }}
                disabled={saving}
                className="px-5 py-2 bg-primary-500 hover:bg-primary-600 disabled:opacity-50 text-white text-sm font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2"
              >
                {saving ? (
                  <>
                    <i className="ri-loader-4-line animate-spin" />
                    Aplicando...
                  </>
                ) : (
                  <>
                    <i className="ri-check-line" />
                    Aplicar
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Fix ALL country code modal (edge function) */}
      {fixAllCountryCodeOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => { if (!fixAllProgress) { setFixAllCountryCodeOpen(false); setFixAllResult(null); } }} />
          <div className="relative bg-white rounded-xl border border-background-200 w-full max-w-[440px] mx-4 p-6 animate-fadeIn">
            {fixAllResult ? (
              <>
                <div className="w-12 h-12 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-4">
                  <i className="ri-check-line text-emerald-600 text-2xl" />
                </div>
                <h3 className="text-lg font-heading font-semibold text-foreground-950 text-center mb-2">
                  Correção concluída!
                </h3>
                <div className="grid grid-cols-3 gap-3 mb-6">
                  <div className="bg-background-50 rounded-xl p-3 text-center border border-background-100">
                    <p className="text-xl font-heading font-bold text-foreground-950">{fixAllResult.total}</p>
                    <p className="text-[10px] text-foreground-500">Total processado</p>
                  </div>
                  <div className="bg-emerald-50 rounded-xl p-3 text-center border border-emerald-100">
                    <p className="text-xl font-heading font-bold text-emerald-700">{fixAllResult.updated}</p>
                    <p className="text-[10px] text-emerald-600">Atualizados</p>
                  </div>
                  <div className="bg-amber-50 rounded-xl p-3 text-center border border-amber-100">
                    <p className="text-xl font-heading font-bold text-amber-700">{fixAllResult.skipped + fixAllResult.errored}</p>
                    <p className="text-[10px] text-amber-600">Ignorados</p>
                  </div>
                </div>
                <div className="flex items-center justify-center">
                  <button
                    onClick={() => { setFixAllCountryCodeOpen(false); setFixAllResult(null); }}
                    className="px-6 py-2.5 bg-primary-500 hover:bg-primary-600 text-white text-sm font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-check-line mr-1.5" />
                    Concluído
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="w-12 h-12 rounded-full bg-primary-50 flex items-center justify-center mx-auto mb-4">
                  <i className="ri-global-line text-primary-600 text-2xl" />
                </div>
                <h3 className="text-lg font-heading font-semibold text-foreground-950 text-center mb-2">
                  Formatar TODOS os telefones (55 + DDD)
                </h3>
                <p className="text-sm text-foreground-500 text-center mb-2">
                  Esta ação irá processar <strong>todos</strong> os {totalCount} contato{totalCount !== 1 ? 's' : ''}{groupFilter ? ' deste grupo' : ''} no <strong>servidor</strong>.
                </p>
                <p className="text-xs text-foreground-400 text-center mb-1">
                  Números sem DDD recebem <strong>55 + DDD</strong>. Números com DDD mas sem 55 recebem <strong>55</strong>.
                </p>
                <p className="text-xs text-foreground-400 text-center mb-1">
                  Números já no formato internacional (12-13 dígitos) são ignorados.
                </p>
                <p className="text-xs text-foreground-400 text-center mb-6">
                  Processamento em lotes de 500 — pode levar alguns segundos.
                </p>

                <div className="space-y-3 mb-6">
                  <label className="text-sm font-medium text-foreground-700">DDD (Código de Área)</label>
                  <select
                    id="fix-all-country-code"
                    className="w-full px-4 py-3 bg-white border-2 border-background-200 rounded-xl text-sm text-foreground-900 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all cursor-pointer font-medium"
                    defaultValue={DEFAULT_DDD}
                  >
                    {BRAZIL_DDDS.map((d) => (
                      <option key={d.code} value={d.code}>{d.code} — {d.state} ({d.region})</option>
                    ))}
                  </select>
                </div>

                <div className="flex items-center justify-center gap-3">
                  <button
                    onClick={() => { setFixAllCountryCodeOpen(false); setFixAllResult(null); }}
                    disabled={fixAllProgress}
                    className="px-5 py-2 bg-white border border-background-200 hover:bg-background-50 text-foreground-700 text-sm font-medium rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={() => {
                      const select = document.getElementById('fix-all-country-code') as HTMLSelectElement;
                      handleFixAllCountryCode(select?.value || '54');
                    }}
                    disabled={fixAllProgress}
                    className="px-5 py-2 bg-primary-500 hover:bg-primary-600 disabled:opacity-50 text-white text-sm font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2"
                  >
                    {fixAllProgress ? (
                      <>
                        <i className="ri-loader-4-line animate-spin" />
                        Processando...
                      </>
                    ) : (
                      <>
                        <i className="ri-rocket-line" />
                        Corrigir TODOS
                      </>
                    )}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      <BulkEditModal
        isOpen={bulkEditOpen}
        onClose={() => setBulkEditOpen(false)}
        onConfirm={handleBulkEditConfirm}
        groups={groups}
        saving={saving}
        selectedCount={effectiveSelectedCount}
      />
    </div>
  );
}